import {
  globalMessageHandler,
  GridModalFormProps,
  useForm,
  Validator,
} from "common";
import I18n from "i18n-js";
import { size } from "lodash";
import { useEffect, useMemo } from "react";
import {
  getSharingService,
  SharingFormItem,
  SharingOptions,
} from "./sharingApi";

export interface UseSharingFormProps
  extends GridModalFormProps<SharingFormItem> {
  defaultValues?: Partial<SharingFormItem>;
}

export default function useSharingForm({
  id,
  afterSave,
  defaultValues,
}: UseSharingFormProps) {
  const service = getSharingService();

  const form = useForm<SharingFormItem, SharingOptions>({
    id,
    ignoreNavigateAfterCreate: true,
    translationCategory: "Sharing",
    defaultValues,
    rules: {
      name: Validator.rule.any().required(),
      location_id: Validator.rule.any().required(),
    },
    afterSave,
    onValidationFailed: () => {
      globalMessageHandler.error(I18n.t("App.formHasErrors"));
    },
    ...service,
  });

  const selectedWaiverNames = useMemo(() => {
    const names = form.options?.waiver_ids
      ?.filter(w =>
        (form.item.waiver_ids as any)?.some(
          (id: any) => String(id) === String(w.value)
        )
      )
      ?.map(w => w.label);
    return names?.join(", ") || "";
  }, [form.options?.waiver_ids, form.item.waiver_ids]);

  const selectedLocationName = useMemo(() => {
    const loc = form.options?.location_id?.find(
      l => String(l.value) === String(form.item.location_id)
    )?.label;
    return loc || "";
  }, [form.options?.location_id, form.item.location_id]);

  // Auto-set location if only one available
  useEffect(() => {
    if (size(form.options?.location_id) === 1) {
      form.setAttributes({
        location_id: form.options?.location_id[0].value,
      });
    }
  }, [form.options?.location_id]);

  // Auto-set name based on location and waivers
  useEffect(() => {
    let name = `${selectedLocationName} ${selectedWaiverNames}`.trim();
    form.setAttributes({ name });
  }, [selectedLocationName, selectedWaiverNames]);

  return form;
}
